
import {
	AddDataSharedFunc, DashboardSharedFilterFunc, DashboardSharedFilterKey,
	IDashboardSharedChartData, IDashboardSharedDimensionsViewModel,
} from "models/common";
import { IFilters, IFilterKeys, DimensionsById, ISourceData, IDimFacility, IDimension, IDimMDT } from "models/core";
import { IDashboardViewModel } from "./viewModel";

//Plots for this dashboard
import * as PrimarySite from "./primarySiteHierarchy";
import * as Morphology from "./morphology";
import * as Remoteness from "../../common/plotdata/remoteness";
import * as HHSOfResidence from "../../common/plotdata/hhsOfResidence";

export type { IDashboardViewModel } from "./viewModel";

export type IDashboardSource = IDashboardViewModel[];

export interface IDashboardDimensionsViewModel extends IDashboardSharedDimensionsViewModel {
    hhsResidenceGroups?: DimensionsById;
    dataQualityIndicators?: DimensionsById;
    monthsArray?: Array<IDimension>;
    yearsArray?: Array<IDimension>;
    months?: { [monthId: number]: string };
    years?: { [yearId: number]: string };
    dqCompFacility?: { [facilityYearKey: string]: number };
    dqCompState?: { [facilityYearKey: string]: number };
    oneYearSurvivalMDT?: { [mdtDiagYearKey: string]: { "MDT": number, "State": number } };
    threeYearSurvivalMDT?: { [mdtDiagYearKey: string]: { "MDT": number, "State": number } };
    fiveYearSurvivalMDT?: { [mdtDiagYearKey: string]: { "MDT": number, "State": number } };
    tenYearSurvivalMDT?: { [mdtDiagYearKey: string]: { "MDT": number, "State": number } };
    kmSurvivalMDT?: {[mdtCancerKey: string]: { survivalTime: string, mdtKMSurvival: number, stateKMSurvival: number }};

}

interface ILevel1ChartData {
	totalDiagnoses: number;
	totalDataQualityEligibleDiagnoses: number;
	facilities: IDimFacility[];
	hhses: IDimension[];
	mdts: IDimMDT[];
	month: IDimension[];
	year: IDimension[];

    histDiag: { numerator, denominator: number, percentage: number };
	craniotomy30DaysDiagnosis: { numerator: number, denominator: number, percentage: number; };
	craniotomyForHistDiagGlioAnaOligoAnaAstro: { numerator: number, denominator: number, percentage: number; };
	craniotomyForepend: { numerator: number, denominator: number, percentage: number; };
	inHospitalMortality: { numerator: number, denominator: number, percentage: number; };
	mortality30Days: { numerator: number, denominator: number, percentage: number; };
	mortality90Days: { numerator: number, denominator: number, percentage: number; };
	oneYearSurvival: { numerator: number, denominator: number, percentage: number; };
	twoYearSurvival: { numerator: number, denominator: number, percentage: number; };
	rt45To54LowGrade: { numerator: number, denominator: number, percentage: number; };
	rt54To60Under70Grade4: { numerator: number, denominator: number, percentage: number };
	rtUnder70HighGrade: { numerator: number, denominator: number, percentage: number };
	rt70PlusHighGrade: { numerator: number, denominator: number, percentage: number };
	rtAnaOligo: { numerator: number, denominator: number, percentage: number };
	rtAnaAstro: { numerator: number, denominator: number, percentage: number };
	rt49DaysHistDiagHighGrade: { numerator: number, denominator: number, percentage: number };
	// Export
	filteredReferralIdCsv: string;
}

// HACK: chop out the reference array from the shared interface 
type RestrictedSharedChartData = Omit<Omit<IDashboardSharedChartData, "facilities">, "hhses">;

interface ILevel2ChartData extends RestrictedSharedChartData {
	totalDiagnoses: number;
	primarySite: PrimarySite.IHierarchyPlotData;
	morphology: Morphology.IPlotData;
	remoteness: Remoteness.IPlotData;
	hhsOfResidence: HHSOfResidence.IPlotData;
}

export interface IChartData {
	level1: ILevel1ChartData;
	level2: ILevel2ChartData;
}

const BreastFilterKeys: IFilterKeys = {
	//Filter keys for breast indicators
	DiagnosisYear: { key: "DiagnosisYear", label: "Diagnosis Year" },
	DiagnosisMonth: { key: "DiagnosisMonth", label: "Diagnosis Month" },
	MdtYear: { key: "MdtYear", label: "MDT Year" },
	MdtMonth: { key: "MdtMonth", label: "MDT Month" },
	Invasive: { key: "Invasive", label: "Invasive / In-situ" },
	PrimarySite: { key: "PrimarySite", label: "Primary site" },
	Morphology: { key: "Morphology", label: "Morphology" },
	Remoteness: { key: "Remoteness", label: "Rurality" },
	DefinitiveMastectomy: { key: "DefinitiveMastectomy", label: "Definitive Mastectomy" },
	HHSOfResidence: { key: "HHSOfResidence", label: "HHS of residence" }
}

export const DashboardFilterKeys: IFilterKeys = {
	...DashboardSharedFilterKey,
	...BreastFilterKeys
}

export type Filters = IFilters<typeof DashboardFilterKeys>;
export type DashboardFilterFunc = DashboardSharedFilterFunc<IDashboardViewModel>;
export type AddDataFunc = AddDataSharedFunc<IDashboardViewModel, ISourceData>;